import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import PropTypes from "prop-types";

const NAVY = "#292E5C";
const ORANGE = "#F7AA47";
const MUTED = "#55636D";
const BORDER = "rgba(85, 99, 109, 0.15)";
const SLOT_DURATION_MINUTES = 15;

const SLOT_GROUPS = [
  { key: "morning", label: "Morning" },
  { key: "afternoon", label: "Afternoon" },
  { key: "evening", label: "Evening" },
];

const SLOT_TEMPLATE = [
  { morning: [], afternoon: ["03:00 PM", "03:15 PM"], evening: [] },
  { morning: [], afternoon: [], evening: [] },
  { morning: ["10:00 AM"], afternoon: ["02:30 PM", "04:00 PM"], evening: [] },
  { morning: [], afternoon: [], evening: ["05:00 PM", "05:30 PM"] },
  { morning: ["09:30 AM", "11:15 AM"], afternoon: [], evening: [] },
  { morning: [], afternoon: [], evening: [] },
  { morning: [], afternoon: ["01:00 PM", "03:45 PM"], evening: ["06:15 PM"] },
];

const addDays = (date, days) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

const getStartOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const formatShortDate = (date) =>
  new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);

const isValidDate = (date) =>
  date instanceof Date && !Number.isNaN(date.getTime());

const formatDayTitle = (date, index, fallbackLabel = "") => {
  if (!isValidDate(date)) return fallbackLabel || `Day ${index + 1}`;
  if (index === 0) return "Today";
  if (index === 1) return "Tomorrow";

  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
  }).format(date);
};

const formatDateBadge = (date, fallbackLabel = "") => {
  if (!isValidDate(date)) return fallbackLabel || "--";

  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "short",
  }).format(date);
};

const formatFullDate = (date) =>
  new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);

const formatTimeLabel = (date) =>
  new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(date);

const parseTimeLabel = (slot) => {
  const match = String(slot).match(/^(\d{1,2}):(\d{2})\s?(AM|PM)$/i);

  if (!match) return null;

  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const meridiem = match[3].toUpperCase();

  if (meridiem === "PM" && hours !== 12) hours += 12;
  if (meridiem === "AM" && hours === 12) hours = 0;

  const date = new Date(2000, 0, 1, hours, minutes, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatSlotRange = (slot) => {
  if (slot.label) return slot.label;

  if (slot.startAt && slot.endAt) {
    const startDate = new Date(slot.startAt);
    const endDate = new Date(slot.endAt);

    if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())) {
      return `${formatTimeLabel(startDate)} - ${formatTimeLabel(endDate)}`;
    }
  }

  const startDate = parseTimeLabel(slot.time);

  if (!startDate) return slot.time;

  const endDate = new Date(startDate.getTime() + SLOT_DURATION_MINUTES * 60 * 1000);
  return `${formatTimeLabel(startDate)} - ${formatTimeLabel(endDate)}`;
};

const normalizeSlot = (slot, groupKey, index) => {
  if (typeof slot === "string") {
    return {
      id: `${groupKey}-${slot}-${index}`,
      time: slot,
      label: formatSlotRange({ time: slot }),
      startAt: "",
      endAt: "",
    };
  }

  return {
    id: slot.id || slot.start_at || `${groupKey}-${slot.time}-${index}`,
    time: slot.time || "",
    label: slot.label || "",
    startAt: slot.start_at || slot.startAt || "",
    endAt: slot.end_at || slot.endAt || "",
  };
};

const normalizeSlotGroups = (slotGroups) => {
  return SLOT_GROUPS.reduce((normalized, group) => {
    const slots = slotGroups?.[group.key] || [];
    normalized[group.key] = slots.map((slot, index) =>
      normalizeSlot(slot, group.key, index)
    );
    return normalized;
  }, {});
};

const getSlotCount = (slots) =>
  SLOT_GROUPS.reduce((total, group) => total + (slots[group.key]?.length || 0), 0);

const createFallbackDays = () => {
  const today = getStartOfToday();

  return SLOT_TEMPLATE.map((slots, index) => {
    const date = addDays(today, index);
    const normalizedSlots = normalizeSlotGroups(slots);

    return {
      date,
      id: date.toISOString(),
      label: formatShortDate(date),
      slotCount: getSlotCount(normalizedSlots),
      slots: normalizedSlots,
    };
  });
};

const normalizeApiDays = (availabilityDays) => {
  if (!Array.isArray(availabilityDays) || availabilityDays.length === 0) {
    return [];
  }

  return availabilityDays.map((day) => {
    const date = new Date(`${day.date}T00:00:00`);
    const slotGroups = SLOT_GROUPS.reduce((groups, group) => {
      const apiGroup = (day.periods || []).find((period) => period.key === group.key);
      groups[group.key] = (apiGroup?.slots || []).map((slot, index) =>
        normalizeSlot(slot, group.key, index)
      );
      return groups;
    }, {});

    return {
      date,
      id: day.date,
      label: Number.isNaN(date.getTime()) ? day.label : formatShortDate(date),
      slotCount: day.slots_available ?? getSlotCount(slotGroups),
      slots: slotGroups,
    };
  });
};

const getCounsellorName = (counsellor) =>
  counsellor?.full_name || counsellor?.name || "Counsellor";

const CouncilBookingDialog = ({
  availabilityDays,
  bookingLoading,
  counsellor,
  error,
  loading,
  onBookSlot,
  onClose,
  onRetry,
  open,
}) => {
  const fallbackDays = useMemo(() => createFallbackDays(), []);
  const apiDays = useMemo(
    () => normalizeApiDays(availabilityDays),
    [availabilityDays]
  );
  const days = apiDays.length ? apiDays : fallbackDays;
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const dayButtonRefs = useRef([]);

  const selectedDay = days[selectedDayIndex] || days[0];
  const counsellorName = getCounsellorName(counsellor);

  // Reset the whole selection only when the dialog opens or the counsellor
  // changes — NOT when availability reloads (e.g. after a failed/cancelled
  // payment), so the chosen date persists.
  useEffect(() => {
    if (open) {
      setSelectedDayIndex(0);
      setSelectedSlot(null);
    }
  }, [open, counsellor]);

  // When availability reloads while the dialog stays open, keep the selected
  // date but drop the slot (the slot list has just refreshed).
  useEffect(() => {
    if (open) {
      setSelectedSlot(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availabilityDays]);

  useEffect(() => {
    if (selectedDayIndex > days.length - 1) {
      setSelectedDayIndex(0);
      setSelectedSlot(null);
    }
  }, [days.length, selectedDayIndex]);

  useEffect(() => {
    if (!open) return;

    const selectedButton = dayButtonRefs.current[selectedDayIndex];

    if (!selectedButton) return;

    window.requestAnimationFrame(() => {
      selectedButton.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    });
  }, [open, selectedDayIndex]);

  const handleDayChange = (index) => {
    setSelectedDayIndex(index);
    setSelectedSlot(null);
  };

  const handlePreviousDay = () => {
    handleDayChange(Math.max(selectedDayIndex - 1, 0));
  };

  const handleNextDay = () => {
    handleDayChange(Math.min(selectedDayIndex + 1, days.length - 1));
  };

  const handleBookSlot = () => {
    if (!selectedSlot || !selectedDay) return;

    onBookSlot({
      counsellor,
      date: selectedDay.date,
      dateLabel: formatFullDate(selectedDay.date),
      slot: selectedSlot.time,
      slotLabel: formatSlotRange(selectedSlot),
      startAt: selectedSlot.startAt,
      endAt: selectedSlot.endAt,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={bookingLoading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      BackdropProps={{
        sx: {
          bgcolor: "rgba(0, 0, 0, 0.45)",
          backdropFilter: "blur(6px)",
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: "calc(100% - 24px)", sm: 589 },
          maxWidth: "calc(100% - 24px)",
          borderRadius: "20px",
          overflow: "hidden",
          bgcolor: "#fff",
          boxShadow: "0px 16px 75px rgba(0, 0, 0, 0.08)",
        },
      }}
    >
      <Box
        sx={{
          alignSelf: "stretch",
          px: { xs: 2.5, sm: 3 },
          py: 2.25,
          position: "relative",
          bgcolor: NAVY,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              color: "#fff",
              fontSize: { xs: 21, sm: 24 },
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 600,
              lineHeight: "36px",
            }}
          >
            Book the slot
          </Typography>
          <Typography
            sx={{
              color: "rgba(255, 255, 255, 0.72)",
              fontSize: 12,
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 500,
              lineHeight: "18px",
            }}
          >
            {counsellorName}
          </Typography>
        </Box>

        <IconButton
          type="button"
          aria-label="Close booking dialog"
          disabled={bookingLoading}
          onClick={onClose}
          sx={{
            width: 32,
            height: 32,
            color: "#fff",
            "&:hover": {
              bgcolor: "rgba(255, 255, 255, 0.12)",
            },
            "&.Mui-disabled": {
              color: "rgba(255, 255, 255, 0.45)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          alignSelf: "stretch",
          background: "#fff",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: { xs: 2.5, sm: 3 },
            py: 1.85,
            bgcolor: "rgba(85, 99, 109, 0.08)",
          }}
        >
          <Typography
            sx={{
              color: "#000",
              fontSize: 18,
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 700,
            }}
          >
            Select Date
          </Typography>
        </Box>

        <Box
          sx={{
            px: { xs: 2.5, sm: 3 },
            pt: 2.5,
            pb: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2.25,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "32px minmax(0, 1fr) 32px",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <IconButton
              type="button"
              aria-label="Previous date"
              disabled={selectedDayIndex === 0 || loading}
              onClick={handlePreviousDay}
              sx={{
                width: 27,
                height: 27,
                border: `1px solid ${NAVY}`,
                color: selectedDayIndex === 0 ? "#A4A8B5" : NAVY,
                bgcolor: "#fff",
                "&:hover": { bgcolor: "#fff" },
                "&.Mui-disabled": {
                  borderColor: "#D3D7E1",
                  color: "#B5B9C5",
                },
              }}
            >
              <KeyboardArrowLeftIcon sx={{ fontSize: 18 }} />
            </IconButton>

            <Box
              role="tablist"
              aria-label="Appointment dates"
              sx={{
                position: "relative",
                display: "flex",
                gap: 1,
                pb: 1.35,
                px: 0.25,
                borderBottom: `1px solid ${BORDER}`,
                overflowX: "auto",
                overflowY: "hidden",
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(41, 46, 92, 0.28) transparent",
                "&::-webkit-scrollbar": { height: 5 },
                "&::-webkit-scrollbar-track": {
                  bgcolor: "rgba(255,255,255,0.45)",
                  borderRadius: 999,
                },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: "rgba(41, 46, 92, 0.28)",
                  borderRadius: 999,
                },
              }}
            >
              {days.map((day, index) => {
                const isSelected = index === selectedDayIndex;

                return (
                  <Button
                    key={day.id}
                    ref={(element) => {
                      dayButtonRefs.current[index] = element;
                    }}
                    type="button"
                    role="tab"
                    aria-selected={isSelected}
                    disabled={loading}
                    onClick={() => handleDayChange(index)}
                    sx={{
                      flex: { xs: "0 0 118px", sm: "1 0 0" },
                      minWidth: { xs: 118, sm: 88 },
                      minHeight: 78,
                      px: 1,
                      py: 1,
                      borderRadius: "12px",
                      border: isSelected
                        ? `1px solid ${ORANGE}`
                        : "1px solid rgba(255, 255, 255, 0.48)",
                      bgcolor: isSelected
                        ? "rgba(247, 170, 71, 0.18)"
                        : "rgba(255, 255, 255, 0.46)",
                      color: NAVY,
                      textTransform: "none",
                      boxShadow: isSelected
                        ? "0 12px 26px rgba(247, 170, 71, 0.24)"
                        : "none",
                      fontFamily: "Montserrat, sans-serif",
                      transition:
                        "background-color 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
                     
                      "&:hover": {
                        bgcolor: isSelected
                          ? "rgba(247, 170, 71, 0.24)"
                          : "rgba(255, 255, 255, 0.9)",
                        boxShadow: isSelected
                          ? "0 12px 26px rgba(247, 170, 71, 0.28)"
                          : "0 8px 18px rgba(41, 46, 92, 0.1)",
                      },
                      "&.Mui-disabled": {
                        color: "rgba(85, 99, 109, 0.55)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.35,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          maxWidth: "100%",
                          color: isSelected ? "#1F243F" : NAVY,
                          fontSize: 13,
                          fontFamily: "Montserrat, sans-serif",
                          fontWeight: 800,
                          lineHeight: "17px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatDayTitle(day.date, index, day.label)}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          color: isSelected ? "#B96500" : MUTED,
                          fontSize: 12,
                          fontFamily: "Montserrat, sans-serif",
                          fontWeight: 700,
                          lineHeight: "16px",
                        }}
                      >
                        {formatDateBadge(day.date, day.label)}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          color:
                            isSelected && day.slotCount > 0
                              ? "#0B7D3B"
                              : isSelected
                              ? "#7A5A24"
                              : day.slotCount > 0
                              ? "rgba(14, 129, 68, 0.9)"
                              : "rgba(85, 99, 109, 0.72)",
                          fontSize: 11,
                          fontFamily: "Montserrat, sans-serif",
                          fontWeight: 600,
                          lineHeight: "15px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {day.slotCount > 0
                          ? `${day.slotCount} slot${day.slotCount > 1 ? "s" : ""}`
                          : "No slots"}
                      </Typography>
                    </Box>
                  </Button>
                );
              })}
            </Box>

            <IconButton
              type="button"
              aria-label="Next date"
              disabled={selectedDayIndex === days.length - 1 || loading}
              onClick={handleNextDay}
              sx={{
                width: 27,
                height: 27,
                bgcolor: NAVY,
                color: "#fff",
                border: `1px solid ${NAVY}`,
                "&:hover": { bgcolor: "#20264F" },
                "&.Mui-disabled": {
                  bgcolor: "#D3D7E1",
                  borderColor: "#D3D7E1",
                  color: "#fff",
                },
              }}
            >
              <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              width: "100%",
              minHeight: 106,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            {loading ? (
              <Box
                sx={{
                  minHeight: 100,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <CircularProgress size={28} sx={{ color: NAVY }} />
              </Box>
            ) : error ? (
              <Box
                sx={{
                  width: "100%",
                  minHeight: 100,
                  border: `1px dashed ${BORDER}`,
                  borderRadius: "10px",
                  bgcolor: "rgba(255, 255, 255, 0.78)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1.25,
                  px: 2,
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    color: MUTED,
                    fontSize: 13,
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 600,
                    lineHeight: "20px",
                  }}
                >
                  {error}
                </Typography>
                <Button
                  type="button"
                  onClick={onRetry}
                  sx={{
                    minWidth: 0,
                    height: 32,
                    px: 2,
                    borderRadius: "7px",
                    bgcolor: NAVY,
                    color: "#fff",
                    textTransform: "none",
                    fontSize: 12,
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 700,
                    "&:hover": { bgcolor: "#20264F" },
                  }}
                >
                  Retry
                </Button>
              </Box>
            ) : selectedDay?.slotCount > 0 ? (
              SLOT_GROUPS.map((group) => {
                const slots = selectedDay.slots[group.key] || [];

                if (!slots.length) return null;

                return (
                  <Box
                    key={group.key}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    {slots.map((slot) => {
                      const isSelected = slot.id === selectedSlot?.id;

                      return (
                        <Button
                          key={slot.id}
                          type="button"
                          disabled={bookingLoading}
                          onClick={() => setSelectedSlot(slot)}
                          sx={{
                            minWidth: 0,
                            px: 1.25,
                            py: 0.65,
                            borderRadius: "4px",
                            border: `0.55px solid ${isSelected ? ORANGE : NAVY}`,
                            bgcolor: isSelected ? ORANGE : "#fff",
                            color: isSelected ? "#EFEFEF" : NAVY,
                            textTransform: "none",
                            textAlign: "center",
                            fontSize: 10,
                            fontFamily: "Montserrat, sans-serif",
                            fontWeight: 700,
                            lineHeight: "16px",
                            boxShadow: isSelected
                              ? "0 8px 16px rgba(247, 170, 71, 0.28)"
                              : "0 4px 12px rgba(41, 46, 92, 0.06)",
                            "&:hover": {
                              bgcolor: isSelected ? ORANGE : "rgba(41, 46, 92, 0.04)",
                              borderColor: isSelected ? ORANGE : NAVY,
                            },
                            "&.Mui-disabled": {
                              color: isSelected ? "#EFEFEF" : NAVY,
                              opacity: 0.65,
                            },
                          }}
                        >
                          {formatSlotRange(slot)}
                        </Button>
                      );
                    })}
                  </Box>
                );
              })
            ) : (
              <Box
                sx={{
                  width: "100%",
                  minHeight: 88,
                  border: `1px dashed ${BORDER}`,
                  borderRadius: "10px",
                  bgcolor: "rgba(255, 255, 255, 0.72)",
                  display: "grid",
                  placeItems: "center",
                  px: 2,
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    color: MUTED,
                    fontSize: 13,
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 600,
                    lineHeight: "20px",
                  }}
                >
                  No slots available on this date. Please select another date.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          alignSelf: "stretch",
          p: { xs: 2.5, sm: 3 },
          borderTop: "1px solid #E0E0E0",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 1,
          bgcolor: "#fff",
        }}
      >
        <Button
          type="button"
          disabled={bookingLoading}
          onClick={onClose}
          sx={{
            height: 46,
            px: 3,
            borderRadius: "10px",
            border: "1px solid #B5B5B5",
            color: "#B5B5B5",
            bgcolor: "#fff",
            textTransform: "none",
            fontSize: 16,
            fontFamily: "Poppins, sans-serif",
            fontWeight: 500,
            "&:hover": {
              bgcolor: "#FAFAFA",
              borderColor: "#A9A9A9",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          type="button"
          disabled={!selectedSlot || loading || bookingLoading}
          onClick={handleBookSlot}
          sx={{
            height: 46,
            px: 3,
            borderRadius: "10px",
            bgcolor: NAVY,
            color: "#fff",
            textTransform: "none",
            fontSize: 15,
            fontFamily: "Poppins, sans-serif",
            fontWeight: 500,
            boxShadow: "none",
            "&:hover": {
              bgcolor: "#20264F",
              boxShadow: "0 10px 22px rgba(41, 46, 92, 0.18)",
            },
            "&.Mui-disabled": {
              bgcolor: "#D7DCE6",
              color: "#fff",
            },
          }}
        >
          {bookingLoading ? "Processing payment..." : "Confirm & Pay"}
        </Button>
      </Box>
    </Dialog>
  );
};

CouncilBookingDialog.propTypes = {
  availabilityDays: PropTypes.arrayOf(PropTypes.object),
  bookingLoading: PropTypes.bool,
  counsellor: PropTypes.shape({
    full_name: PropTypes.string,
    name: PropTypes.string,
  }),
  error: PropTypes.string,
  loading: PropTypes.bool,
  onBookSlot: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onRetry: PropTypes.func,
  open: PropTypes.bool.isRequired,
};

CouncilBookingDialog.defaultProps = {
  availabilityDays: [],
  bookingLoading: false,
  counsellor: null,
  error: "",
  loading: false,
  onRetry: undefined,
};

export default CouncilBookingDialog;
